import React, { useState } from 'react';
import { UserPlus, PencilSimple, Power, Copy, CheckCircle } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { PageHeader, Bezel, Table, Button, IconButton, Modal, Input, Select, Tag, StatCard, MotionReveal } from '_ui';

const isEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || ''));
const TIER_LABEL = { chinh_thuc: 'Chính thức', thu_viec: 'Thử việc' };
const TIER_OPTIONS = [{ value: 'chinh_thuc', label: 'Chính thức' }, { value: 'thu_viec', label: 'Thử việc' }];

export function NhanVien() {
    const { data, loading, refetch } = useFetch('/api/employees');
    const api = useApi();
    const alert = useAlertActions();
    const [editing, setEditing] = useState(null);     // employee | {} (new) | null
    const [credentials, setCredentials] = useState(null); // { email, password }
    const [showInactive, setShowInactive] = useState(false);

    const employees = data || [];
    const activeCount = employees.filter(e => e.active).length;
    const rows = employees.filter(e => showInactive || e.active);

    async function toggleActive(emp) {
        const res = await api.patch(`/api/employees/${emp._id}`, { active: !emp.active });
        if (res.ok) { alert.success(emp.active ? 'Đã tắt tài khoản' : 'Đã bật tài khoản'); refetch(); }
        else alert.error('Không thể cập nhật', res.error);
    }

    const columns = [
        { key: 'name', header: 'Tên', render: e => <span className={e.active ? 'font-medium text-espresso' : 'text-espresso-soft/60'}>{e.name}</span> },
        { key: 'email', header: 'Email', render: e => <span className="text-espresso-soft">{e.email}</span> },
        { key: 'tier', header: 'Loại', align: 'center', render: e => <Tag tone={(e.employmentType || 'chinh_thuc') === 'thu_viec' ? 'brass' : 'neutral'}>{TIER_LABEL[e.employmentType] || 'Chính thức'}</Tag> },
        { key: 'status', header: 'Trạng thái', align: 'center', render: e => <Tag tone={e.active ? 'sage' : 'neutral'}>{e.active ? 'Đang hoạt động' : 'Đã tắt'}</Tag> },
        { key: 'actions', header: '', align: 'right', render: e => (
            <div className="flex justify-end gap-1">
                <IconButton icon={PencilSimple} label="Sửa" variant="ghost" onClick={() => setEditing(e)} />
                <IconButton icon={Power} label={e.active ? 'Tắt' : 'Bật'} variant="ghost"
                    className={e.active ? 'hover:text-danger' : 'hover:text-sage-deep'} onClick={() => toggleActive(e)} />
            </div>
        ) },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Quản lý"
                title="Nhân viên"
                description="Thêm nhân viên, chỉnh thông tin và bật/tắt tài khoản đăng nhập."
                actions={
                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setShowInactive(v => !v)}>
                            {showInactive ? 'Ẩn tài khoản đã tắt' : 'Hiện tài khoản đã tắt'}
                        </Button>
                        <Button icon={UserPlus} onClick={() => setEditing({})}>Thêm nhân viên</Button>
                    </div>
                }
            />

            <div className="grid gap-5 sm:grid-cols-2">
                <MotionReveal><StatCard label="Đang hoạt động" value={activeCount} /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Tổng nhân viên" value={employees.length} /></MotionReveal>
            </div>

            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={rows} loading={loading} empty={showInactive ? 'Chưa có nhân viên nào.' : 'Không có tài khoản đang hoạt động.'} />
            </Bezel>

            {editing && (
                <EmployeeModal
                    employee={editing}
                    onClose={() => setEditing(null)}
                    onSaved={(creds) => { setEditing(null); refetch(); if (creds) setCredentials(creds); }}
                />
            )}
            {credentials && <CredentialsModal creds={credentials} onClose={() => setCredentials(null)} alert={alert} />}
        </div>
    );
}

function EmployeeModal({ employee, onClose, onSaved }) {
    const isEdit = !!employee._id;
    const api = useApi();
    const alert = useAlertActions();
    const [form, setForm] = useState({ name: employee.name || '', email: employee.email || '', password: '', employmentType: employee.employmentType || 'chinh_thuc' });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

    function validate() {
        const e = {};
        if (!form.name.trim()) e.name = 'Nhập tên';
        if (!isEmail(form.email)) e.email = 'Email không hợp lệ';
        if (!isEdit && form.password && form.password.length < 6) e.password = 'Ít nhất 6 ký tự';
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function save() {
        if (!validate()) return;
        setSaving(true);
        let res;
        if (isEdit) {
            res = await api.patch(`/api/employees/${employee._id}`, { name: form.name.trim(), email: form.email.trim(), employmentType: form.employmentType });
        } else {
            const body = { name: form.name.trim(), email: form.email.trim(), employmentType: form.employmentType };
            if (form.password) body.password = form.password;
            res = await api.post('/api/employees', body);
        }
        setSaving(false);
        if (res.ok) {
            alert.success(isEdit ? 'Đã lưu nhân viên' : 'Đã thêm nhân viên');
            const generated = !isEdit && res.data?.generatedPassword;
            onSaved(generated ? { email: form.email.trim(), password: res.data.generatedPassword } : null);
        } else {
            alert.error('Không thể lưu', res.error);
        }
    }

    return (
        <Modal open onClose={onClose} eyebrow="Nhân viên" title={isEdit ? 'Sửa nhân viên' : 'Thêm nhân viên'}
            footer={<><Button variant="ghost" onClick={onClose}>Huỷ</Button><Button onClick={save} loading={saving}>Lưu</Button></>}>
            <div className="space-y-5">
                <Input label="Họ tên" value={form.name} onChange={e => set('name', e.target.value)} error={errors.name} placeholder="VD: Nguyễn Văn A" />
                <Input label="Email đăng nhập" type="email" value={form.email} onChange={e => set('email', e.target.value)} error={errors.email} placeholder="a@sunnynail.io.vn" />
                <Select label="Loại nhân viên" value={form.employmentType} onChange={v => set('employmentType', v)} options={TIER_OPTIONS} hint="Thử việc chỉ bán được bảng dịch vụ hoa hồng thấp (tối đa 30%)." />
                {!isEdit && (
                    <Input label="Mật khẩu (để trống sẽ tự tạo)" value={form.password} onChange={e => set('password', e.target.value)} error={errors.password} hint="Nếu để trống, hệ thống tạo mật khẩu ngẫu nhiên để bạn đưa cho nhân viên." />
                )}
            </div>
        </Modal>
    );
}

function CredentialsModal({ creds, onClose, alert }) {
    function copy() {
        if (navigator.clipboard) navigator.clipboard.writeText(`Email: ${creds.email}\nMật khẩu: ${creds.password}`).then(() => alert.success('Đã sao chép'));
    }
    return (
        <Modal open onClose={onClose} eyebrow="Tài khoản mới" title="Mật khẩu đăng nhập"
            footer={<Button icon={CheckCircle} onClick={onClose}>Đã lưu lại</Button>}>
            <div className="flex items-start gap-3 rounded-2xl bg-brass/10 p-4 ring-1 ring-brass/20">
                <p className="text-sm text-espresso-soft">
                    Đưa thông tin này cho nhân viên. <span className="font-semibold text-espresso">Mật khẩu chỉ hiện một lần.</span>
                </p>
            </div>
            <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between rounded-2xl bg-cream-200/60 px-4 py-3 ring-1 ring-espresso/10">
                    <span className="text-sm text-espresso-soft">Email</span>
                    <span className="font-medium text-espresso">{creds.email}</span>
                </div>
                <div className="flex items-center justify-between rounded-2xl bg-cream-200/60 px-4 py-3 ring-1 ring-espresso/10">
                    <span className="text-sm text-espresso-soft">Mật khẩu</span>
                    <span className="tnum font-semibold tracking-wide text-espresso">{creds.password}</span>
                </div>
                <button onClick={copy} className="inline-flex items-center gap-2 text-sm font-medium text-sage-deep transition-colors hover:text-espresso">
                    <Copy size={16} weight="light" /> Sao chép email &amp; mật khẩu
                </button>
            </div>
        </Modal>
    );
}
